import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { useFormat } from "@/lib/format";
import type { StockRow } from "@/services/production";
import { createInventory, fetchInventories, fetchKitHistory } from "@/services/stockLedger";
import { ReadOnlyArea } from "@/components/ReadOnly";

// Z Production · "📋 Inventaire des kits" and "Historique du stock des kits". The count makes the
// shelf what was counted — only the kits typed in, a blank one is not counted; every kit change is
// listed with where it came from.
const WH = [{ id: 1, name: "Penthalaz" }, { id: 67, name: "Dielsdorf" }, { id: 4, name: "Berlin" }] as const;
const INV_WH_KEY = "myotaku-warehouse:inv-wh";

export default function StockCountAndHistory({ stock }: { warehouseId: number | null; stock: StockRow[] }) {
  return (
    <>
      <Count stock={stock} />
      <History />
    </>
  );
}

function Count({ stock }: { stock: StockRow[] }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { zurichDateTime } = useFormat();
  const [wh, setWhState] = useState<number>(() => { try { return Number(localStorage.getItem(INV_WH_KEY)) || 1; } catch { return 1; } });
  const setWh = (w: number) => { setWhState(w); try { localStorage.setItem(INV_WH_KEY, String(w)); } catch { /* this tab only */ } };
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [asking, setAsking] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const last = useQuery({ queryKey: ["inventories", wh], queryFn: () => fetchInventories(wh) });
  useEffect(() => { setCounted({}); setDone(null); }, [wh]);
  const app = (s: StockRow) => s.warehouses?.[String(wh)]?.qty ?? 0;
  const keys = stock.filter((s) => (counted[s.code] ?? "") !== "");
  const diffs = keys.filter((s) => Number(counted[s.code]) !== app(s));
  const name = WH.find((w) => w.id === wh)!.name;
  const save = useMutation({
    mutationFn: () => createInventory({ warehouse_id: wh, lines: keys.map((s) => ({ kit_code: s.code, counted: Number(counted[s.code]) || 0 })) }),
    onSuccess: () => {
      setDone(t("inv.done", { w: name, n: keys.length, d: diffs.length }));
      setCounted({});
      ["inventories", "stock-movements", "kit-history", "production", "stock-audit"].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
    },
  });
  const latest = last.data?.inventories[0];

  return (
    <section className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
      <h2 className="border-b border-slate-100 px-5 py-3 font-medium text-slate-900">{t("inv.title")}</h2>
      <div className="px-5 py-3">
        <p className="mb-2 text-xs text-slate-500">{t("inv.hint")}</p>
        <div className="mb-2 flex gap-1">
          {WH.map((w) => (
            <button key={w.id} onClick={() => setWh(w.id)} aria-pressed={wh === w.id}
              className={`rounded-lg px-3 py-1 text-sm ${wh === w.id ? "bg-slate-900 text-white" : "text-slate-600 ring-1 ring-slate-200"}`}>{w.name}</button>
          ))}
        </div>
        <p className="mb-2 text-xs text-slate-500">
          {latest ? t("inv.lastAt", { w: name, when: zurichDateTime(latest.counted_at), who: latest.counted_by || "—", n: latest.lines.length, d: latest.lines.filter((l) => l.delta !== 0).length })
            : t("inv.noneAt", { w: name })}
        </p>
        {/* The count writes the shelf: greyed for someone who only reads Production; the warehouse
            choice and the last count above stay open. */}
        <ReadOnlyArea module="production">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs text-slate-500">
              <th className="py-1 text-left font-medium">{t("inv.kit")}</th>
              <th className="py-1 text-right font-medium">{t("inv.app")}</th>
              <th className="py-1 text-right font-medium">{t("inv.countedCol")}</th>
              <th className="py-1 text-right font-medium">{t("inv.gap")}</th>
            </tr>
          </thead>
          <tbody>
            {stock.map((s, i) => {
              const v = counted[s.code] ?? "";
              const d = v === "" ? null : Number(v) - app(s);
              return (
                <tr key={s.code} className="border-b border-slate-50">
                  <td className="py-1"><span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ background: s.colour ?? "#888" }} />{s.label || s.code}</td>
                  <td className="py-1 text-right tabular-nums text-slate-500">{app(s)}</td>
                  <td className="py-1 text-right">
                    <input ref={(el) => { inputs.current[i] = el; }} value={v} inputMode="numeric" placeholder="—"
                      aria-label={`${s.label || s.code} ${t("inv.counted")}`}
                      onChange={(e) => { setDone(null); setCounted({ ...counted, [s.code]: e.target.value.replace(/\D/g, "") }); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); inputs.current[i + 1]?.focus(); } }}
                      className="w-16 rounded border border-slate-300 px-2 py-0.5 text-right tabular-nums" />
                  </td>
                  <td className={`py-1 text-right text-xs tabular-nums ${d == null || d === 0 ? "text-slate-300" : d > 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {d == null ? "" : d > 0 ? `+${d}` : d || "="}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-2 text-sm text-slate-600">{keys.length ? t("inv.summary", { n: keys.length, d: diffs.length }) : t("inv.start")}</p>
        {save.error && <p className="mt-2 text-sm text-red-600">{(save.error as Error).message}</p>}
        {done && <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900">✅ {done}</p>}
        <button disabled={save.isPending || keys.length === 0} onClick={() => setAsking(true)}
          className="mt-2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-40">{t("inv.save")}</button>
        {asking && (
          <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
            <p className="font-medium">{t("inv.ask", { w: name })}</p>
            <p className="text-xs">{t("inv.askDetail", { n: keys.length, d: diffs.length })}{diffs.length ? ` : ${diffs.map((s) => `${s.label || s.code} ${app(s)} → ${counted[s.code]}`).join(", ")}` : ""}</p>
            <div className="mt-2 flex gap-2">
              <button onClick={() => { setAsking(false); save.mutate(); }} className="rounded bg-amber-700 px-3 py-1 text-sm font-semibold text-white">{t("inv.yes")}</button>
              <button onClick={() => setAsking(false)} className="text-sm underline">{t("unit.no")}</button>
            </div>
          </div>
        )}
        </ReadOnlyArea>
      </div>
    </section>
  );
}

// His "Historique du stock des kits": the last 40 kit changes — kit, warehouse, why, before → after.
function History() {
  const { t } = useT();
  const { zurichDateTime } = useFormat();
  const moves = useQuery({ queryKey: ["kit-history"], queryFn: fetchKitHistory });
  const rows = moves.data?.movements.slice(0, 40) ?? [];
  const why = (s: string) => (["transfer", "delivery", "inventory"].includes(s) ? ` (${t(`inv.src.${s}`)})` : "");
  return (
    <section className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
      <h2 className="border-b border-slate-100 px-5 py-3 font-medium text-slate-900">{t("inv.history")}</h2>
      <p className="px-5 pt-2 text-xs text-slate-500">{t("inv.historyHint")}</p>
      {rows.length === 0 && <p className="px-5 py-4 text-sm text-slate-400">{t("inv.noHistory")}</p>}
      <ul className="divide-y divide-slate-50">
        {rows.map((m) => (
          <li key={m.id} className="flex flex-wrap items-baseline justify-between gap-3 px-5 py-1.5 text-sm">
            <span className="text-slate-700">
              {m.label || m.kit_code} — {WH.find((w) => w.id === m.myotaku_warehouse_id)?.name ?? `#${m.myotaku_warehouse_id}`}{why(m.source)} : {m.previous_qty} → {m.qty}{" "}
              <span className={m.qty > m.previous_qty ? "text-emerald-700" : m.qty < m.previous_qty ? "text-red-700" : "text-slate-400"}>
                {m.qty > m.previous_qty ? "↑" : m.qty < m.previous_qty ? "↓" : "="}
              </span>
            </span>
            <span className="font-mono text-xs text-slate-400">{zurichDateTime(m.at)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
